import { useQuery } from '@tanstack/react-query';

import { CountryCode } from 'common/api-types/common';
import { isNotNilInvariantAsync } from 'common/utils/invariant';
import {
  fetchNutritionalMetadata,
  fetchProductById,
  fetchCatalogUiProduct,
} from 'features/catalog/api';
import { CATALOG_KEY_ROOT } from 'features/catalog/const';

export function useNutritionalMetadataQuery() {
  return useQuery({
    queryKey: [CATALOG_KEY_ROOT, 'nutritionalMetadata'],
    queryFn: () => fetchNutritionalMetadata(),
    staleTime: Infinity,
  });
}

export function useProductQuery(productId: string | undefined) {
  const product = useQuery({
    queryKey: [CATALOG_KEY_ROOT, productId],
    queryFn: () =>
      isNotNilInvariantAsync(
        productId,
        'useProductQuery should not be called with missing productId, verify "enabled" condition',
        (productId) => fetchProductById(productId),
      ),
    enabled: !!productId,
    // so default form values are stable
    staleTime: Infinity,
  });

  const nutritionalMetadata = useNutritionalMetadataQuery();

  return { product, nutritionalMetadata };
}

export function useCatalogProductQuery(
  productId: string | undefined,
  countryCode: CountryCode,
  languageCode: string,
) {
  const product = useQuery({
    enabled: !!productId,
    queryKey: [CATALOG_KEY_ROOT, productId, countryCode, languageCode],
    queryFn: () =>
      isNotNilInvariantAsync(
        productId,
        'useCatalogProductQuery should not be called with missing productId, verify "enabled" condition',
        (productId) => fetchCatalogUiProduct(productId, countryCode, languageCode),
      ),
    staleTime: Infinity,
  });

  const nutritionalMetadata = useNutritionalMetadataQuery();

  return { product, nutritionalMetadata };
}
